import { useCallback, useEffect, useRef, useState } from "react";
import type React from "react";

interface UseGoopyPressOptions {
  disabled?: boolean;
  profile?: "default" | "subtle" | "none";
}

/**
 * Tactile press animation — squish on press, stretch-snap on release.
 * Returns style + pointer handlers to spread onto any element.
 */
export function useGoopyPress({
  disabled,
  profile = "default",
}: UseGoopyPressOptions = {}) {
  const [style, setStyle] = useState<React.CSSProperties>({});
  const bounceTimers = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      bounceTimers.current.forEach(clearTimeout);
    };
  }, []);

  const clearBounceTimers = () => {
    bounceTimers.current.forEach(clearTimeout);
    bounceTimers.current = [];
  };

  const onPointerDown = useCallback(() => {
    if (disabled || profile === "none") return;
    clearBounceTimers();

    const downTransform =
      profile === "subtle" ? "scale(0.985, 0.992)" : "scale(0.95, 0.98)";
    const downTransition =
      profile === "subtle"
        ? "transform 90ms cubic-bezier(0.4, 0, 0.2, 1)"
        : "transform 100ms cubic-bezier(0.4, 0, 0.2, 1)";

    // Press in
    setStyle({
      transform: downTransform,
      transition: downTransition,
    });
  }, [disabled, profile]);

  const onPointerUp = useCallback(() => {
    if (disabled || profile === "none") return;
    clearBounceTimers();

    const upTransform = profile === "subtle" ? "scaleX(1.006)" : "scaleX(1.02)";
    const upTransition =
      profile === "subtle"
        ? "transform 100ms cubic-bezier(0.2, 0, 0.3, 1)"
        : "transform 120ms cubic-bezier(0.2, 0, 0.3, 1)";
    const settleTransition =
      profile === "subtle"
        ? "transform 140ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        : "transform 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";
    const settleDelay = profile === "subtle" ? 90 : 120;

    // Release
    setStyle({
      transform: upTransform,
      transition: upTransition,
    });

    // Snap back
    const t1 = setTimeout(() => {
      setStyle({
        transform: "",
        transition: settleTransition,
      });
    }, settleDelay);

    bounceTimers.current = [t1];
  }, [disabled, profile]);

  const onPointerLeave = useCallback(() => {
    if (disabled || profile === "none") return;
    clearBounceTimers();
    const settleTransition =
      profile === "subtle"
        ? "transform 140ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
        : "transform 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)";

    // Reset when pointer leaves mid-press
    setStyle({
      transform: "",
      transition: settleTransition,
    });
  }, [disabled, profile]);

  const pressStyle: React.CSSProperties = disabled || profile === "none"
    ? {}
    : {
        ...style,
        willChange: "transform",
        transformOrigin: "center center",
      };

  return { style: pressStyle, onPointerDown, onPointerUp, onPointerLeave };
}
